import React, { useRef, useEffect, useLayoutEffect, useMemo } from 'react'
import styled from 'styled-components'
import MessageItem from './MessageItem'
import MessageInput from './MessageInput'
import MessagePreloader from './MessagePreloader'
import { useInboxStore } from '../../../../../stores/inbox/useInboxStore'
import { useInboxHandlers } from '../../../../../services/actions/inbox/useInboxHandlers'
import { useMinSkeletonDuration } from '../skeleton/useMinSkeletonDuration'
import { inboxContentReveal } from '../skeleton/inboxSkeletonStyles'

const Content: React.FC<{ $centered?: boolean }> = ({ $centered }) => {
    const messagesListRef = useRef<HTMLDivElement>(null)
    // Snapshot scroll position before prepending older messages so we can restore it
    const scrollAnchorRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null)
    /** Bottom message identity — unchanged when older messages are prepended; changes on send / new tail message */
    const prevBottomMessageKeyRef = useRef<string | undefined>(undefined)
    const { store: { fetchingMessages, fetchingMoreMessages, fetchingConversations, messages, selectedConversation, messageAttachments } } = useInboxStore()
    const { getMessagesFirstPage, loadMoreMessages, setMessageAttachments } = useInboxHandlers()

    useEffect(() => {
        if (selectedConversation) {
            getMessagesFirstPage(messagesListRef)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedConversation?.conversationId])

    useEffect(() => {
        prevBottomMessageKeyRef.current = undefined
    }, [selectedConversation?.conversationId])

    const messagesLoading = fetchingMessages || (fetchingConversations && !!selectedConversation)
    const showMessagesSkeleton = useMinSkeletonDuration(messagesLoading)

    const scrollToLatest = () => {
        const el = messagesListRef.current
        if (!el) return
        el.scrollTop = el.scrollHeight
    }

    // Keep skeleton static — no scrollable overflow while placeholders are shown.
    useLayoutEffect(() => {
        if (showMessagesSkeleton && messagesListRef.current) {
            messagesListRef.current.scrollTop = 0
        }
    }, [showMessagesSkeleton])

    // Scroll to newest message once real content replaces the skeleton (or on new tail message).
    useLayoutEffect(() => {
        if (showMessagesSkeleton || fetchingMoreMessages || !messagesListRef.current || !messages?.length) return

        const last = messages[messages.length - 1]
        const key = String(last?.tempId ?? last?.messageId ?? '')
        const conversationKey = selectedConversation?.conversationId ?? ''

        if (!key) return

        const bottomKey = `${conversationKey}:${key}`
        if (bottomKey === prevBottomMessageKeyRef.current) return

        prevBottomMessageKeyRef.current = bottomKey
        scrollToLatest()
    }, [messages, showMessagesSkeleton, fetchingMoreMessages, selectedConversation?.conversationId])

    useEffect(() => {
        if (messageAttachments.length && messagesListRef.current) {
            scrollToLatest()
        }
    }, [messageAttachments])

    // Snapshot before older messages are prepended
    useEffect(() => {
        if (fetchingMoreMessages && messagesListRef.current) {
            scrollAnchorRef.current = {
                scrollHeight: messagesListRef.current.scrollHeight,
                scrollTop: messagesListRef.current.scrollTop,
            }
        }
    }, [fetchingMoreMessages])

    // Restore scroll position after older messages are prepended
    useEffect(() => {
        if (!fetchingMoreMessages && scrollAnchorRef.current && messagesListRef.current) {
            const { scrollHeight: prevHeight, scrollTop: prevTop } = scrollAnchorRef.current
            const added = messagesListRef.current.scrollHeight - prevHeight
            messagesListRef.current.scrollTop = prevTop + added
            scrollAnchorRef.current = null
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchingMoreMessages])

    const removeAttachment = (attachment: any) => {
        setMessageAttachments(messageAttachments.filter((a: any) => a._id !== attachment._id))
    }

    const messageSkeletonItems = useMemo(
        () => [
            { sent: false, bubbleWidth: 132 },
            { sent: true, bubbleWidth: 108 },
            { sent: false, bubbleWidth: 156 },
            { sent: true, bubbleWidth: 96 },
            { sent: false, bubbleWidth: 124 },
        ],
        [],
    )

    const preloaders = messageSkeletonItems.map((item, i) => (
        <MessagePreloader key={i} sent={item.sent} bubbleWidth={item.bubbleWidth} />
    ))
    const morePreloaders = [<MessagePreloader key="a" bubbleWidth={112} />, <MessagePreloader key="b" sent bubbleWidth={88} />]

    /** While conversation list is reloading, don't show old messages (selectedConversation may still be the previous thread). */
    const messageContent = showMessagesSkeleton ? (
        <MessagesSkeleton>{preloaders}</MessagesSkeleton>
    ) :
        messages?.length ? (
            <MessagesContentReveal>
                {messages.map((msg: any) => (
                    <div key={msg.messageId || msg.tempId}>
                        <MessageItem message={msg} />
                    </div>
                ))}
            </MessagesContentReveal>
        ) : (
            <NoMessages>
                <NoMsgIcon>✉</NoMsgIcon>
                <NoMsgTitle>No Messages Found</NoMsgTitle>
                <NoMsgText>We're experiencing difficulty retrieving your messages. This may be caused by a weak internet connection or because the conversation has been deleted.</NoMsgText>
            </NoMessages>
        )

    const handleMessagesScroll = (event: React.UIEvent<HTMLDivElement>) => {
        if (showMessagesSkeleton) return
        void loadMoreMessages(event)
    }

    return (
        <Container>
            <MessagesList
                ref={messagesListRef}
                onScroll={handleMessagesScroll}
                $centered={$centered}
                $lockScroll={showMessagesSkeleton}
            >
                {fetchingMoreMessages && morePreloaders}
                {selectedConversation && messageContent}
            </MessagesList>

            {messageAttachments.map((att: any) => (
                <AttachmentRow key={att._id}>
                    <AttachmentName>{att.name} ({(att.byteSize / 1000).toFixed(2)} KB)</AttachmentName>
                    <AttachmentRemove onClick={() => removeAttachment(att)}>×</AttachmentRemove>
                </AttachmentRow>
            ))}

            <InputSection>
                <InputInner $centered={$centered}>
                    <MessageInput />
                </InputInner>
            </InputSection>
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 0;
    min-height: 0;
    width: 100%;
    border-radius: 12px;
    padding: 1px 12px 12px 12px;
    background-color: #fafafa;
    /* allow Assistant / Refine dropdown to paint above the message list (Popper "up") */
    overflow: visible;
`
const MessagesList = styled.div<{ $centered?: boolean; $lockScroll?: boolean }>`
    flex: 1;
    min-height: 0;
    overflow-y: ${({ $lockScroll }) => ($lockScroll ? 'hidden' : 'auto')};
    overflow-x: hidden;
    overscroll-behavior: contain;
    padding: 5px;
    margin-bottom: 4px;
    ${({ $centered }) => $centered ? `
        max-width: 860px;
        width: 100%;
        margin-left: auto;
        margin-right: auto;
    ` : ''}

    scrollbar-width: thin;
    scrollbar-color: #e8eaed transparent;

    &::-webkit-scrollbar { width: 4px; background: transparent; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: #e8eaed; border-radius: 2px; }
`

const InputSection = styled.div`
    width: 100%;
    background: transparent;
    padding: 0 12px 12px;
    border-top: none;
    flex-shrink: 0;
`

const InputInner = styled.div<{ $centered?: boolean }>`
    width: 100%;
    ${({ $centered }) => $centered ? `
        max-width: 860px;
        margin: 0 auto;
    ` : ''}
`
const MessagesSkeleton = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    min-height: 100%;
    overflow: hidden;
    box-sizing: border-box;
    padding-bottom: 8px;
`
const MessagesContentReveal = styled.div`
    ${inboxContentReveal}
`
const NoMessages = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-top: 80px;
    text-align: center;
    padding: 0 40px;
`
const NoMsgIcon = styled.div`
    font-size: 48px;
    opacity: 0.3;
    margin-bottom: 16px;
`
const NoMsgTitle = styled.p`
    font-size: 22px;
    font-weight: bold;
    color: ${props => props.theme.primaryColor};
    margin-bottom: 12px;
`
const NoMsgText = styled.p`
    font-size: 14px;
    color: #8c96ad;
    line-height: 1.6;
`
const AttachmentRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f6f8fb;
    border-radius: 20px;
    padding: 8px 16px;
    margin-bottom: 4px;
    font-size: 13px;
    font-weight: bold;
    color: ${props => props.theme.primaryColor};
`
const AttachmentName = styled.span``
const AttachmentRemove = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    font-size: 18px;
    color: #8c96ad;
    &:hover { color: #ff6760; }
`

export default Content
